import { defineStore } from "pinia";
import { generateCacheKey, clearCache } from "@/composables/useCache";
import { store } from "@/stores/global.js";
import { doFetch } from "@/composables/doFetch";

export const useCompanyInfoStore = defineStore("companyInfo", {
    state: () => ({
        companyInfo: {},
        isLoading: false,
        error: null,
        lastFetchTime: {}, // Add timestamp tracking for cache
        fetchInProgress: {}, // Track ongoing fetch operations
        cacheTimeMs: 3600000, // Cache company info for 1 hour (companies don't change often)
    }),
    actions: {
        async callCompanyProfileAPI(symbol, token) {
            const url = new URL(`https://${store.env}.quotemedia.com/v3/companies/profile`);

            url.searchParams.append("token", token);
            url.searchParams.append("webmaster_id", store.wmid);
            if (store.sid) url.searchParams.append("sid", store.sid);
            url.searchParams.append("module", "datatool");
            url.searchParams.append("symbols", symbol);

            return await doFetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        },
        async fetchCompanyInfo(symbol, token) {
            if (!symbol || !token) return null;

            const normalizedSymbol = symbol.trim().toUpperCase();
            const cacheKey = generateCacheKey(normalizedSymbol);

            // Check if we already have the company info cached
            if (this.companyInfo[cacheKey]) {
                return this.companyInfo[cacheKey];
            }

            // If already fetching this symbol, wait for that request
            if (this.fetchInProgress[cacheKey]) {
                return this.fetchInProgress[cacheKey];
            }

            this.isLoading = true;
            this.error = null;

            const fetchPromise = (async () => {
                try {
                    const response = await this.callCompanyProfileAPI(normalizedSymbol, token);
                    const data = response;

                    if (data?.results?.companies?.[0]) {
                        const instrumentType = (
                            data.results.companies[0].profile?.details?.instrumentType || "Equity"
                        ).toLowerCase();

                        const companyInfo = {
                            instrumentType,
                            ...data.results.companies[0],
                        };

                        // Store with cache key
                        this.companyInfo[cacheKey] = companyInfo;
                        this.lastFetchTime[cacheKey] = Date.now();

                        return companyInfo;
                    } else {
                        // Set default instrument type if data not found
                        const companyInfo = {
                            instrumentType: "equity",
                        };

                        // Store with cache key
                        this.companyInfo[cacheKey] = companyInfo;
                        this.lastFetchTime[cacheKey] = Date.now();

                        return companyInfo;
                    }
                } catch (error) {
                    console.error("Error fetching company info:", error);
                    this.error = error;
                    return { instrumentType: "equity" }; // Return default on error
                } finally {
                    this.isLoading = false;
                    delete this.fetchInProgress[cacheKey];
                }
            })();

            this.fetchInProgress[cacheKey] = fetchPromise;
            return fetchPromise;
        },

        getInstrumentType(symbol) {
            if (!symbol) return "equity";

            const cacheKey = generateCacheKey(symbol.trim().toUpperCase());
            return this.companyInfo[cacheKey]?.instrumentType || "equity";
        },

        /**
         * Clear cached data for specific symbols or all symbols
         * @param {string|string[]|null} symbols - Symbols to clear or null for all
         */
        clearCache(symbols = null) {
            // Create filters object based on parameters
            const filters = {};
            if (symbols !== null) {
                filters[0] = Array.isArray(symbols) ? symbols : symbols.split(",");
            }

            clearCache(
                {
                    dataCache: this.companyInfo,
                    timeCache: this.lastFetchTime,
                },
                {
                    filters: Object.keys(filters).length > 0 ? filters : null,
                }
            );
        },
    },
});
